/** The child details a family reads back, in words rather than the stored values. */
import { formatChecklistDate } from '~/pages/FamilyDetail/checklistWords';

export type DetailAnswers = Record<string, unknown>;

/** The fields asked with plain inputs; the rest are the medical questions. */
export const PLAIN_FIELDS = ['dateOfBirth', 'gender', 'currentSchool'];

export const GENDERS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
];

export const FIELD_LABELS: Record<string, string> = {
  dateOfBirth: 'Birth date',
  gender: 'Gender',
  currentSchool: 'Current school',
  medicalCondition: 'Medical conditions',
  specialNeeds: 'Special needs',
  insuranceInfo: 'Insurance',
  otcMedicationPermission: 'Over-the-counter medicine',
  otcMedicationsApproved: 'Approved medicines',
};

const MEDICINES: [string, string][] = [
  ['acetaminophen', 'Acetaminophen'],
  ['ibuprofen', 'Ibuprofen'],
  ['coughDrops', 'Cough drops'],
];

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** One field as the family reads it; empty when nothing was answered. */
export function detailWords(field: string, answers: DetailAnswers): string {
  const value = answers[field];
  switch (field) {
    case 'dateOfBirth':
      return text(value) ? formatChecklistDate(text(value)) : '';
    case 'gender':
      return GENDERS.find((gender) => gender.value === value)?.label ?? '';
    case 'otcMedicationPermission':
      return value === true ? 'Yes' : value === false ? 'No' : '';
    case 'otcMedicationsApproved': {
      const approved = record(value);
      const other = text(approved.other);
      return [...MEDICINES.filter(([key]) => approved[key] === true).map(([, name]) => name), ...(other ? [other] : [])].join(', ');
    }
    case 'insuranceInfo': {
      const insurance = record(value);
      return [insurance.carrier, insurance.groupNumber, insurance.policyNumber, insurance.policyHolderName].map(text).filter(Boolean).join(' · ');
    }
    default:
      return text(value);
  }
}
